import { useDisclosure } from '@mantine/hooks';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { IOrder } from '../interfaces/Order';
import { IStore } from '../interfaces/Store';
import { buildOrderFolio } from '../utils/ordersUtils';
import { useRequestCashierPinValidation } from './useRequestCashierPinValidation';

type PinUser = {
  _id: string;
  name: string;
  cashierBox: string;
  permission: any;
} | null;

type Options = {
  store?: IStore | null;
};

export const useOrderPaymentLinesModal = ({ store }: Options) => {
  const { t } = useTranslation(['OrdersPage']);
  const { requestPin } = useRequestCashierPinValidation();
  const [opened, handlers] = useDisclosure(false);
  const [order, setOrder] = useState<IOrder | null>(null);
  const [pinUser, setPinUser] = useState<PinUser>(null);

  const open = useCallback(async (nextOrder: IOrder | null | undefined) => {
    if (!nextOrder) return false;
    if (store?.requireCashierPinToCollect) {
      const res = await requestPin();
      if (!res?._id) return false;
      setPinUser(res);
    }
    setOrder(nextOrder);
    handlers.open();
    return true;
  }, [handlers, requestPin, store?.requireCashierPinToCollect]);

  const close = useCallback(() => {
    setPinUser(null);
    setOrder(null);
    handlers.close();
  }, [handlers]);

  const syncOrder = useCallback((nextOrder: IOrder | null | undefined) => {
    if (!nextOrder?._id) return;

    setOrder((currentOrder) => {
      if (!currentOrder?._id) return currentOrder;
      if (String(currentOrder._id) !== String(nextOrder._id)) return currentOrder;
      return nextOrder;
    });
  }, []);

  const title = order
    ? `${t('OrdersPage:paymentLinesModal.title')}: ${buildOrderFolio(
        order?.folioPrefix,
        order?.sequence
      )}`
    : t('OrdersPage:paymentLinesModal.title');

  return {
    opened,
    order,
    pinUser,
    title,
    open,
    close,
    syncOrder
  };
};
